import {
  RESERVED_LABEL_NAME_ERROR,
  getReservedLabelValueError,
  isAssignableReservedLabelName,
  isReservedLabelName,
} from '@ipfabric/utils/labels';
import {
  AUTO_ASSIGNMENT_RULE_FILTER_COLUMNS,
  type AutoAssignmentRuleFilterColumn,
  type AutoAssignmentRuleTargetType,
  isAutoAssignmentRuleFilterColumn,
} from '@ipfabric/utils/schema/labels/autoAssignmentRuleFilters';
import { isKeyInObject } from '@ipfabric/utils/types/isKeyInObject';
import { z } from 'zod';

const ruleConditionOperatorSchema = z.enum(['is', 'isNot']);

const FILTER_COLUMN_LABELS: Record<AutoAssignmentRuleFilterColumn, string> = {
  hostname: 'Hostname',
  vendor: 'Vendor',
  family: 'Family',
  model: 'Model',
  platform: 'Platform',
  version: 'Version',
  sn: 'Serial number',
  intName: 'Interface name',
  intDscr: 'Interface description',
};

export const RULE_TEST_COLUMNS = {
  device: ['hostname', 'sn'],
  intL2: ['hostname', 'intName'],
} as const satisfies Record<
  AutoAssignmentRuleTargetType,
  readonly AutoAssignmentRuleFilterColumn[]
>;

export const testColumnLabel = (column: string): string =>
  isKeyInObject(column, FILTER_COLUMN_LABELS)
    ? FILTER_COLUMN_LABELS[column]
    : column;

type ConditionProperty = AutoAssignmentRuleFilterColumn;

const isConditionProperty = (value: unknown): value is ConditionProperty =>
  typeof value === 'string' &&
  (isAutoAssignmentRuleFilterColumn('device', value) ||
    isAutoAssignmentRuleFilterColumn('intL2', value));

const isRuleTargetType = (
  value: unknown,
): value is AutoAssignmentRuleTargetType =>
  typeof value === 'string' &&
  isKeyInObject(value, AUTO_ASSIGNMENT_RULE_FILTER_COLUMNS);

const ruleConditionPropertySchema = z.custom<ConditionProperty>(
  isConditionProperty,
  { message: 'Invalid condition property.' },
);

const ruleConditionSchema = z.object({
  property: ruleConditionPropertySchema,
  operator: ruleConditionOperatorSchema,
  regex: z.string().min(1, 'Regex pattern is required.'),
});

const ruleLabelSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Label name is required.')
    .max(64)
    .regex(/^[a-zA-Z0-9_-]+$/)
    .superRefine((value, ctx) => {
      if (isAssignableReservedLabelName(value) || !isReservedLabelName(value)) {
        return;
      }
      ctx.addIssue({
        code: 'custom',
        message: RESERVED_LABEL_NAME_ERROR,
      });
    }),
  value: z.string().trim().max(128).default(''),
});

export const ruleFormSchema = z.object({
  type: z.custom<AutoAssignmentRuleTargetType>(isRuleTargetType),
  description: z.string().trim(),
  conditions: z
    .array(ruleConditionSchema)
    .min(1, 'At least one condition is required.'),
  labels: z
    .array(ruleLabelSchema)
    .min(1, 'At least one label is required.')
    .superRefine((labels, ctx) => {
      // Reported on the list so RuleLabelsEditor shows it next to the labels.
      const message = labels
        .map((label) => getReservedLabelValueError(label.name, label.value))
        .find((error) => error !== null);
      if (message) {
        ctx.addIssue({ code: 'custom', message });
      }
    }),
  inherit: z.boolean(),
});

export type RuleFormValues = z.input<typeof ruleFormSchema>;
export type RuleFormSubmitValues = z.output<typeof ruleFormSchema>;

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'is', label: 'Is' },
  { value: 'isNot', label: 'Is not' },
] as const;
